<template>
  <div class="xy-lucky-wheel" ref="componentRef">
    <lucky-grid
      :key="upKey"
      ref="luckyWheelRef"
      :width="wheelCfg.width"
      :height="wheelCfg.height"
      :rows="wheelCfg.rows"
      :cols="wheelCfg.cols"
      :prizes="wheelCfg.prizes"
      :blocks="wheelCfg.blocks"
      :buttons="wheelCfg.buttons"
      :defaultStyle="wheelCfg.defaultStyle"
      :activeStyle="wheelCfg.activeStyle"
      :defaultConfig="wheelCfg.defaultConfig"
      @end="endCallback" />
    <div class="check-box" :style="checkBoxStyle">
      <template v-if="showCustomCheck">
        <img :style="checkboxImgStyle" :src="checkSrc" @click="handleCheckChange" />
      </template>
      <template v-else>
        <input type="checkbox" id="myCheckbox" @change="handleCheckChange" />
        <label for="myCheckbox"></label>
      </template>
      <span class="check-label">{{ checkLabel }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { info, defaultProps } from './config'
import { LuckyGrid } from '@lucky-canvas/vue'
import { useLucky } from '@/packages/components/hook/useLucky'
import { computeRatioPx, computeRatio } from '@/packages/plugins/utils'
import EmptyImg from '@/packages/components/business/luckyGrid/src/assets/img/empty.png'
import useAppStore from '@/h5/stores/app'

defineOptions({ name: info.name })
const props = defineProps(defaultProps)
const appStore = useAppStore ? useAppStore() : null

// 转盘配置
const wheelCfg = ref({
  width: '200px',
  height: '200px',
  rows: 3,
  cols: 3,
  blocks: [
    {
      background: 'rgba(0,0,0,0)',
      padding: '2px'
    }
  ],
  prizes: [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 2, y: 0 },
    { x: 2, y: 1 },
    { x: 2, y: 2 },
    { x: 1, y: 2 },
    { x: 0, y: 2 },
    { x: 0, y: 1 }
  ],
  buttons: [],
  defaultStyle: {
    borderRadius: Infinity,
    background: '#bac5ee'
  },
  activeStyle: {
    background: '#f7cf9f'
  },
  defaultConfig: {
    speed: 20,
    accelerationTime: 1500,
    decelerationTime: 1500
  }
})

/**
 * 计算奖项周围的坐标
 * @param {number} rows 行数
 * @param {number} cols 列数
 */
const calculateSurroundingCoordinates = (rows, cols) => {
  const prizes = []
  const col = 1
  // 计算高度比例
  const row = 1

  // 上边
  for (let x = 0; x < cols; x++) {
    prizes.push({ x, y: 0, col, row })
  }

  // 右边
  for (let y = 1; y < rows; y++) {
    prizes.push({ x: cols - 1, y, col, row })
  }

  // 下边
  if (rows > 1) {
    for (let x = cols - 2; x >= 0; x--) {
      prizes.push({ x, y: rows - 1, col, row })
    }
  }

  // 左边
  if (cols > 1) {
    for (let y = rows - 2; y > 0; y--) {
      prizes.push({ x: 0, y, col, row })
    }
  }
  return prizes
}

/** 初始化转盘配置 */
const initWheel = () => {
  // 转盘宽高
  wheelCfg.value.width = computeRatioPx(props.width)
  wheelCfg.value.height = computeRatioPx(props.height)
  // 转盘行列
  wheelCfg.value.rows = props.rows
  wheelCfg.value.cols = props.cols

  /**
   * 奖项,位子动态计算分割
   * 1. col和row为奖项大小默认为1
   * 2. x和y为奖项的位置
   * 3. 根据props.rows和props.cols计算奖项的位置，奖项大小默认为1，x和y从0开始，顺时针计算
   */
  let prizes = calculateSurroundingCoordinates(props.rows, props.cols)
  wheelCfg.value.prizes = prizes

  // 设置中心图片
  if (props.buttonSrc) {
    const centerBtn = { x: 1, y: 1, col: props.cols - 2, row: props.rows - 2 }
    wheelCfg.value.buttons = [centerBtn]
    wheelCfg.value.buttons[0].imgs = [{ src: props.buttonSrc, width: '100%', height: '100%' }]
    wheelCfg.value.buttons[0].background = 'rgba(0,0,0,0)'
  }

  // 奖项背景颜色
  // wheelCfg.value.defaultStyle.background = 'rgba(0,0,0,0)'
  wheelCfg.value.defaultStyle.borderRadius = props.prizesBorderRadius ? computeRatioPx(props.prizesBorderRadius) : 0
  if (props.prizesActiveBackgroundSrc) {
    prizes = prizes.map((item) => {
      item.imgs = [{ src: EmptyImg, activeSrc: props.prizesActiveBackgroundSrc, width: '100%', height: '100%' }]
      return item
    })
    wheelCfg.value.prizes = prizes
  }
  // 奖项选中背景颜色
  wheelCfg.value.activeStyle.background = props.prizesActiveBackgroundColor || ''
  wheelCfg.value.defaultStyle.background = props.prizesBackgroundColor || 'rgba(0,0,0,0)'
  // 格子间距
  wheelCfg.value.defaultConfig.gutter = computeRatio(Number(props.gutter))
  // 旋转速度
  wheelCfg.value.defaultConfig.speed = Number(props.speed) || 20
  // 减速时间
  wheelCfg.value.defaultConfig.decelerationTime = Number(props.decelerationTime)
  // 开始时间
  wheelCfg.value.defaultConfig.accelerationTime = Number(props.accelerationTime)
}

/** 设置奖项 */
const setPrizes = (sortedAndMergedAwards, language) => {
  props.prizes.forEach((item, index) => {
    const top = computeRatioPx(item.top)
    const languageItem = item.languages.find((item) => item.language === language)
    let src = languageItem?.src
    if (languageItem.srcFeMale && languageItem.srcMale) {
      src = appStore?.pageDataSource['data-source_userConstant']?.sex === 2 ? languageItem.srcFeMale : languageItem.srcMale
    } else if (languageItem.srcMale) {
      src = languageItem.srcMale
    }
    const id = sortedAndMergedAwards && sortedAndMergedAwards[index] ? sortedAndMergedAwards[index].id : 0
    const rewards = sortedAndMergedAwards && sortedAndMergedAwards[index] ? sortedAndMergedAwards[index].rewards : []
    const prizeImg = { src, top, width: computeRatioPx(item.width), height: computeRatioPx(item.height) }
    if (wheelCfg.value.prizes[index] && src) {
      wheelCfg.value.prizes[index].id = id
      wheelCfg.value.prizes[index].rewards = rewards
      if (wheelCfg.value.prizes[index]?.imgs?.length) {
        wheelCfg.value.prizes[index].imgs.unshift(prizeImg)
      } else {
        wheelCfg.value.prizes[index].imgs = [prizeImg]
      }
    }
  })
}

const {
  componentRef,
  luckyWheelRef,
  upKey,
  checkLabel,
  checkSrc,
  endCallback,
  showCustomCheck,
  checkboxImgStyle,
  handleCheckChange,
  checkBoxStyle,
  start,
  stop,
  getConfig,
  startRoulette,
  againRoulette,
  closeDialog,
  refreshMoneyBalances
} = useLucky(props, wheelCfg, initWheel, setPrizes)

defineExpose({
  start,
  stop,
  getConfig,
  startRoulette,
  againRoulette,
  closeDialog,
  refreshMoneyBalances
})
</script>

<style lang="scss" scoped>
.xy-lucky-wheel {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.check-box {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 45px;
  margin-top: 40px;
  box-sizing: border-box;
  .check-label {
    color: #fff;
  }
  #myCheckbox {
    display: none;
  }
  #myCheckbox + label::before {
    content: '';
    display: inline-block;
    width: 14px;
    height: 14px;
    margin-right: 10px;
    background: transparent; /* 默认背景颜色 */
    border-radius: 3px; /* 圆角 */
    vertical-align: middle;
    border: 1px solid #beabec;
  }

  /* 当复选框被选中时改变背景和添加勾选符号 */
  #myCheckbox:checked + label::before {
    background: transparent; /* 选中时的背景颜色 */
    content: '✓';
    color: #beabec; /* 勾选符号颜色 */
    font-size: 16px;
    text-align: center;
    line-height: 14px; /* 调整勾选符号的位置 */
  }
}
</style>
